import { isEqual } from 'lodash'
import {
  TEXT_BOX_ROLE,
  TEXT_INPUT_ROLE,
} from '@wix/wix-data-client-common-standalone'
import { FieldType } from '@wix/wix-data-schema-types'
import type { ComponentDatabindingApiFactory } from './types'
import { baseComponentBindingApi } from './baseComponentBindingApi'
import type { RecordStoreRecord } from '../record-store/service'
import { appContext } from '../viewer-app-module/DataBindingAppContext'
import {
  isReadOnly,
  selectCurrentRecord,
} from '../dataset-controller/rootReducer'
import { AppError } from '../logger'
import { transformFromViewToRecord } from '../components/transformData'
import { updateComponentProps } from '../components/helpers/componentValueUtils'
import { getFieldValue } from '../components/helpers'
import type { AdaptedComponent } from '../inverted-dependencies/components'
import type { InputConnectionConfig } from '../types'
import { isNonEmptyConfig } from '../components/helpers/connectionConfigUtils'
import { convertToFieldType } from '../helpers/schemaTypes'

// Browsers may populate these components without the component ever emitting a
// change event (Safari AutoFill / "Hide My Email" is the known case), so their
// value has to be reconciled with the record before saving.
const AUTOFILLABLE_ROLES: string[] = [TEXT_INPUT_ROLE, TEXT_BOX_ROLE]

// These components hold their value as a string, so the record value can only
// be restored from them for fields that are stored as a string themselves (or
// as a number, which is coerced back by the schema types). For anything else -
// a date, for instance - what the component holds is a display value that
// cannot be reversed into a record value.
const SYNCABLE_FIELD_TYPES: FieldType[] = [
  FieldType.text,
  FieldType.number,
  FieldType.url,
  FieldType.email,
]

const isEmptyValue = (value: any) =>
  value === undefined || value === null || value === ''

export const inputBindingApi: ComponentDatabindingApiFactory<
  AdaptedComponent,
  InputConnectionConfig
> = (component, connectionConfig, context) => {
  const { logger, errorReporting, features } = appContext
  const {
    getState,
    getSchema,
    getFieldType,
    actions,
    eventListeners,
    modeIsLivePreview,
  } = context

  const setFieldOnComponentChange = () => {
    const { id, role } = component
    const { properties } = connectionConfig

    component.onChange(
      errorReporting((event: any) => {
        const propName = properties.checked ? 'checked' : 'value'
        const fieldName = properties[propName].fieldName
        const record = selectCurrentRecord(getState())

        const value = transformFromViewToRecord({
          value: event.target[propName],
          currentValue: getFieldValue(record, fieldName),
          fieldType: getFieldType(fieldName).getOrElse(undefined),
          fieldName,
          utils: {
            referenceFetcher: (value: any, fieldName: string) =>
              actions.fetchRecordById(value, fieldName).getOrElse(value),
          },
          role,
        })

        actions.setFieldInCurrentRecordAndSynchronize(fieldName, value, id)
      }, AppError.withMessage('Input adapter onChange - setting value to record failed')),
    )
  }

  // The record is normally fed by the component's change event only. When the
  // value got into the component without such an event, the record would keep
  // its previous (usually empty) value while the component displays the new
  // one, and the save would silently persist the stale record. Reading the
  // component right before saving closes that gap.
  const syncValueFromComponentBeforeSave = () => {
    const { id, role } = component
    const { properties } = connectionConfig

    if (!AUTOFILLABLE_ROLES.includes(role) || !properties.value) {
      return
    }

    const { fieldName, format } = properties.value
    const fieldType = getFieldType(fieldName).getOrElse(undefined)

    // a formatted binding puts a display value into the component, which cannot
    // be turned back into a record value
    if (format || !fieldType || !SYNCABLE_FIELD_TYPES.includes(fieldType)) {
      return
    }

    const syncValue = errorReporting(() => {
      const record = selectCurrentRecord(getState())

      if (!record) {
        return
      }

      const componentValue = component.getValue({ propPath: 'value' })
      const currentValue = getFieldValue(record, fieldName)

      if (isEmptyValue(componentValue) && isEmptyValue(currentValue)) {
        return
      }

      // the component holds strings, so both sides have to be brought to the
      // schema's type before they can be told apart - otherwise a number field
      // would look changed on every save ('1' vs 1)
      const value = convertToFieldType(fieldType, componentValue)

      // what the component holds is not a number at all, so there is nothing to
      // restore from it - writing NaN would only corrupt the record
      if (typeof value === 'number' && Number.isNaN(value)) {
        return
      }

      if (isEqual(value, currentValue)) {
        return
      }

      actions.setFieldInCurrentRecordAndSynchronize(fieldName, value, id)
    }, AppError.withMessage('Input adapter onBeforeSave - syncing value to record failed'))

    // registered directly on the event listeners rather than through
    // datasetApi.onBeforeSave, which asserts the dataset's type and mode and is
    // meant for user code. the callback must not resolve to false - that is how
    // a beforeSave hook cancels the save - so nothing is returned from it
    eventListeners.register('beforeSave', () => {
      syncValue()
    })
  }

  const updateComponentFromCurrentRecord = (
    previousRecord?: RecordStoreRecord,
  ) => {
    updateComponentProps({
      currentRecord: selectCurrentRecord(getState()),
      previousRecord,
      component,
      connectionConfig,
      modeIsLivePreview,
      getFieldType,
      getSchema,
      logger,
      recordStore: context.recordStore,
      actions: context.actions,
    })
  }

  const syncValidityIndication = () => {
    const pristine = actions.isCurrentRecordPristine(getState())
    const newRecord = actions.isCurrentRecordNew(getState())
    if (pristine && newRecord) {
      component.resetValidityIndication()
    }
  }

  return {
    ...baseComponentBindingApi(component, connectionConfig, context),
    isValidConnection() {
      return isNonEmptyConfig(connectionConfig)
    },
    bind() {
      setFieldOnComponentChange()

      if (
        features.syncTextInputValuesBeforeSave &&
        !isReadOnly(getState()) &&
        // in live preview components deliberately keep their value when the
        // record's one is empty, so reading them back would resurrect values
        // the record does not have
        !modeIsLivePreview
      ) {
        syncValueFromComponentBeforeSave()
      }

      if (connectionConfig.filters) {
        component.onChange(
          errorReporting(
            actions.refresh,
            AppError.withMessage(
              'Input adapter onChange - sync actions failed',
            ),
          ),
        )
      }
    },

    async onRecordsLoaded() {
      updateComponentFromCurrentRecord()
      syncValidityIndication()
    },

    async onCurrentViewChanged() {
      updateComponentFromCurrentRecord()
      syncValidityIndication()
    },

    async onCurrentIndexChanged() {
      updateComponentFromCurrentRecord()
      syncValidityIndication()
    },

    async onCurrentRecordModified(previousRecord) {
      updateComponentFromCurrentRecord(previousRecord)
      syncValidityIndication()
    },
  }
}
